// Where something brought onto a workspace by dropping it was let go, for
// the board showing that workspace to put what it brings there (its card's
// head under the pointer) rather than wherever the board would have packed
// it; and, while it is carried over the board, where it is, for the board to
// show where it would land. A board hears these as they happen; nothing is
// kept for one opened later.
const listeners = new Map();
const channel = (kind, workspaceId) => `${kind}\n${workspaceId}`;
const tell = (kind, workspaceId, payload) => {
  for (const fn of [...(listeners.get(channel(kind, workspaceId)) || [])]) fn(payload);
};
const listen = (kind, workspaceId, fn) => {
  if (!workspaceId || typeof fn !== "function") return () => {};
  const key = channel(kind, workspaceId);
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(fn);
  return () => {
    const set = listeners.get(key);
    if (!set) return;
    set.delete(fn);
    if (!set.size) listeners.delete(key);
  };
};

// The card keys a dropped item (dragTypes' droppedItem) brings onto a board:
// a session's (a finding's) by its id, a machine's and a workspace's as the
// board keys their cards.
export function dropKeys(item) {
  if (!item) return [];
  if (item.kind === "workspace") return item.id ? [`graph:workspace:${item.id}`] : [];
  if (item.kind === "machine") return item.id ? [`graph:machine:${item.id}`] : [];
  return (item.sessions || []).filter((s) => s?.id).map((s) => String(s.id).toLowerCase());
}

// What a carried item is called, for the place it would land.
export function dropTitle(item) {
  if (!item) return "";
  if (item.kind === "machine") return item.name || "";
  if (item.kind === "finding") return item.sessionTitle || item.title || "";
  return item.title || item.sessions?.[0]?.title || "";
}

export function noteBoardDrop(workspaceId, item, clientX, clientY) {
  const keys = dropKeys(item);
  if (!workspaceId || !keys.length || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
  tell("drop", workspaceId, { keys, clientX, clientY, title: dropTitle(item) });
}

export function onBoardDrop(workspaceId, fn) {
  return listen("drop", workspaceId, fn);
}

// Carried over the board (`item` and where), or no longer (`item` null: it
// left, was let go, or the drag ended anywhere).
let overNow = null;
export function noteBoardDragOver(workspaceId, item, clientX, clientY) {
  if (!workspaceId) return;
  const keys = dropKeys(item);
  if (!item || !keys.length || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    if (overNow === workspaceId) overNow = null;
    tell("over", workspaceId, null);
    return;
  }
  if (overNow && overNow !== workspaceId) tell("over", overNow, null);
  overNow = workspaceId;
  tell("over", workspaceId, { keys, title: dropTitle(item), clientX, clientY });
}

export function onBoardDragOver(workspaceId, fn) {
  return listen("over", workspaceId, fn);
}

// However a drag ends (let go over a board or anywhere else, or given up
// with Escape), no board keeps showing where it would have landed.
if (typeof window !== "undefined") {
  const ended = () => {
    if (!overNow) return;
    const was = overNow;
    overNow = null;
    tell("over", was, null);
  };
  window.addEventListener("dragend", ended, true);
  window.addEventListener("drop", ended, true);
}
